import React, { useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import Button from 'components/shared/Button';
import Alert from 'components/shared/Alert';
import Spinner from 'components/shared/Spinner';

import crops from 'domains/crops';

import canvases from 'domains/canvases';
import activeEditor from 'domains/active_editor';
import cartProductCreator from 'domains/cart_product_creator';

import { selectPriceTotal } from 'selectors/prices';
import { selectCartPhotos } from 'selectors/cart_photos';

import type { SharedAddToCartProps } from './AddToCartWrapper';

function StandardAddToCart(props: SharedAddToCartProps) {
  const { disable } = props;

  const dispatch = useAppDispatch();
  const canvasIds = useAppSelector(canvases.selectors.selectIds);
  const total = useAppSelector(selectPriceTotal);
  const cartPhotos = useAppSelector(selectCartPhotos);
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);
  const isProcessing = useAppSelector(cartProductCreator.selectors.isProcessing);
  const isCartProduct = useAppSelector(activeEditor.selectors.isCartProduct);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const productNeedsPhoto = useMemo(() => {
    const multinode = canvasIds.length === 1 && potentialCropIds.length > 1;
    return !multinode && potentialCropIds.length > 0;
  }, [potentialCropIds, canvasIds]);

  const productHasPhoto = useMemo(() => cartPhotos.length > 0, [cartPhotos]);

  function dispatchAddToCart() {
    if (isCartProduct) {
      dispatch(cartProductCreator.actions.saveCartProductEdit.pending());
    } else {
      dispatch(cartProductCreator.actions.addToCart.pending('standard'));
    }
  }

  function handleAddToCartClick() {
    if (potentialCropIds.length > cartPhotos.length) {
      setShowConfirmDialog(true);
    } else {
      dispatchAddToCart();
    }
  }

  const cropsNotReady =
    (productNeedsPhoto && cartPhotos.length !== potentialCropIds.length) ||
    (productNeedsPhoto && !productHasPhoto);

  return (
    <React.Fragment>
      <Button
        type="cornered"
        className="cart_button"
        id="add-to-cart-button"
        onClick={handleAddToCartClick}
        disabled={disable || cropsNotReady}
      >
        {!isProcessing && <span>{isCartProduct ? 'Save' : 'Add to cart'}</span>}
        {!isProcessing && !isCartProduct && <span className="price">| ${total.toFixed(2)}</span>}
        {isProcessing && <Spinner />}
      </Button>

      {showConfirmDialog &&
        renderModal(
          <Alert
            title="Confirm Add to Cart"
            labelledBy="add-to-cart-alert-modal"
            lastFocusedElementId="add-to-cart-button"
            onClose={() => setShowConfirmDialog(false)}
            onCancel={() => setShowConfirmDialog(false)}
            message={
              'Product is missing one or more photos.\nAre you sure you want to add it to the cart?'
            }
            onContinue={() => {
              dispatchAddToCart();
              setShowConfirmDialog(false);
            }}
          />
        )}
    </React.Fragment>
  );
}

export default StandardAddToCart;
